import { useState } from "react";
import { ThumbsUp, ThumbsDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface ThumbsRatingProps {
  toolCategory: "Copy Engine" | "VBR Fact Selection" | "VBR Email Generation";
  stationId: string;
}

export function ThumbsRating({ toolCategory, stationId }: ThumbsRatingProps) {
  const [selectedRating, setSelectedRating] = useState<boolean | null>(null);
  const { toast } = useToast();

  const submitRating = useMutation({
    mutationFn: async (rating: boolean) => {
      return apiRequest('/api/tool-ratings', 'POST', { toolCategory, rating, stationId });
    },
    onSuccess: (_, rating) => {
      setSelectedRating(rating);
      toast({
        title: "Thanks for your feedback!",
        description: rating ? "Glad this was helpful." : "We'll work on improving this.",
      });
    },
    onError: () => {
      toast({
        title: "Failed to submit rating",
        description: "Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleClick = (rating: boolean) => {
    if (selectedRating === rating) return;
    submitRating.mutate(rating);
  };

  return (
    <div className="flex items-center gap-1">
      <Button
        variant="ghost"
        size="sm"
        className={`h-8 w-8 p-0 ${selectedRating === true ? 'text-green-600 bg-green-50 hover:bg-green-100' : 'text-gray-400 hover:text-green-600'}`}
        onClick={() => handleClick(true)}
        disabled={submitRating.isPending}
        title="Thumbs up"
      >
        <ThumbsUp className={`h-4 w-4 ${selectedRating === true ? 'fill-current' : ''}`} />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className={`h-8 w-8 p-0 ${selectedRating === false ? 'text-red-600 bg-red-50 hover:bg-red-100' : 'text-gray-400 hover:text-red-600'}`}
        onClick={() => handleClick(false)}
        disabled={submitRating.isPending}
        title="Thumbs down"
      >
        <ThumbsDown className={`h-4 w-4 ${selectedRating === false ? 'fill-current' : ''}`} />
      </Button>
    </div>
  );
}
